import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { InventoryItem } from '../common/inventory-item';
import { SelectedItem } from '../item-selector/interface';
import { PackageContentsService } from './package-contents.service';

@Injectable()
export class ItemConflictService {
  constructor(private packageContentsService: PackageContentsService) {}

  resolveItemConflicts(
    selectedItem: SelectedItem,
    availableItems: SelectedItem[],
    partnerId: string,
    marketId: string,
  ): Observable<SelectedItem[]> {
    const duplicateItem = availableItems.find((i) => i.itemId === selectedItem.itemId);
    if (duplicateItem) {
      return of([]);
    }

    const packageItemsMap$ = this.getPackagedItemsMap(selectedItem, availableItems, partnerId, marketId);
    return packageItemsMap$.pipe(
      map((packageItemsMap) => {
        const itemsToCheck = packageItemsMap.get(selectedItem.itemId) ?? [selectedItem];
        packageItemsMap.delete(selectedItem.itemId);
        return this.getItemsToSelect(selectedItem, itemsToCheck, packageItemsMap, availableItems);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getPackagedItemsMap(
    selectedItem: SelectedItem,
    availableItems: SelectedItem[],
    partnerId: string,
    marketId: string,
  ): Observable<Map<string, InventoryItem[]>> {
    const availablePackageItems = availableItems.filter((i) => i.type === 'package');
    const allPackageIds = availablePackageItems.map((i) => i.itemId);
    if (selectedItem.type === 'package') {
      allPackageIds.push(selectedItem.itemId);
    }
    return this.packageContentsService.fetchMultiPackageItems(allPackageIds, partnerId, marketId);
  }

  private getItemsToSelect(
    selectedItem: SelectedItem,
    itemsToCheck: SelectedItem[],
    packageItemsMap: Map<string, InventoryItem[]>,
    availableItems: SelectedItem[],
  ): SelectedItem[] {
    const allProductItems = availableItems.filter((i) => i.type !== 'package');
    const allPackagedItems = Array.from(packageItemsMap.values()).flat();
    const allAvailableItems = allProductItems.concat(allPackagedItems);
    const uniqueItems = itemsToCheck.filter((i) => !allAvailableItems.some((ai) => ai.itemId === i.itemId));
    if (uniqueItems.length === itemsToCheck.length) {
      return [selectedItem];
    }
    return uniqueItems;
  }
}
